// Facebook Import Friends Web Component
import ProtectedComponent from './protected-component.js';
import { escapeHtml } from '../utils/html-utils.js';
import { openGoogleDrivePicker } from './google-drive-picker.js';
import logger from '../utils/logger.js';
import { renderPageTitle } from '../utils/emoji-badge.js';

class FacebookImportFriends extends ProtectedComponent {
  constructor() {
    super();
    this.jobs = [];
    this.isLoading = false;
    this.isStartingImport = false;
    this.errorMessage = null;
    this.successMessage = null;
    this.pollingInterval = null;
    this.googleDriveAccounts = [];
    this.isLoadingAccounts = false;
    this.friendsFileId = null;
    this.friendsFileName = null;
  }

  connectedCallback() {
    super.connectedCallback();
    this.render();
    this.attachEventListeners();
    this.loadUserAndJobs();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }
  }

  async loadUserAndJobs() {
    try {
      if (!window.API) {
        throw new Error('API not available');
      }

      const authData = await window.API.request('/auth/status');
      if (!authData.authenticated) {
        throw new Error('User not authenticated');
      }

      this.currentUser = authData.user;
      await Promise.all([
        this.loadJobs(),
        this.loadGoogleDriveAccounts()
      ]);
    } catch (error) {
      logger.error('Failed to load user or jobs:', error);
      this.errorMessage = 'Failed to load import jobs';
      this.isLoading = false;
      this.render();
    }
  }

  async loadGoogleDriveAccounts() {
    try {
      if (!window.API || !this.currentUser) {
        return;
      }

      this.isLoadingAccounts = true;
      const data = await window.API.request(`/users/settings/${this.currentUser.id}/media-sources`);
      this.googleDriveAccounts = data.accounts?.['google-drive'] || [];
      this.isLoadingAccounts = false;
      this.render();
    } catch (error) {
      logger.error('Failed to load Google Drive accounts:', error);
      this.isLoadingAccounts = false;
    }
  }

  async openFilePicker() {
    if (this.googleDriveAccounts.length === 0) {
      this.errorMessage = 'Please connect a Google Drive account first in Sparkle Sources settings.';
      this.render();
      return;
    }

    const account = this.googleDriveAccounts[0];
    try {
      await openGoogleDrivePicker(this, 'google-drive', account.accountIdentifier, 'DOCS', (data) => {
        if (data.action === google.picker.Action.PICKED) {
          const file = data.docs[0];
          if (file) {
            this.friendsFileId = file.id;
            this.friendsFileName = file.name || 'Selected File';
            this.errorMessage = null;
            this.render();
          }
        }
      }, false, 'your_friends.json');
    } catch (error) {
      logger.error('Failed to open file picker:', error);
      this.errorMessage = error.message || 'Failed to open Google Drive file picker. Please try again.';
      this.render();
    }
  }

  async loadJobs() {
    try {
      if (!window.API) {
        throw new Error('API not available');
      }

      const data = await window.API.request('/facebook-import/jobs');
      // Filter to only friends import jobs
      this.jobs = (data.jobs || []).filter(job => job.importType === 'friends_only');
      this.isLoading = false;
      this.render();
    } catch (error) {
      logger.error('Failed to load import jobs:', error);
      this.errorMessage = 'Failed to load import jobs';
      this.isLoading = false;
      this.render();
    }
  }

  async startImport() {
    if (!this.friendsFileId) {
      this.errorMessage = 'Please select the friends JSON file';
      this.render();
      return;
    }

    this.isStartingImport = true;
    this.errorMessage = null;
    this.successMessage = null;
    this.render();

    try {
      const response = await window.API.request('/facebook-import/start', {
        method: 'POST',
        body: JSON.stringify({
          importType: 'friends_only',
          friendsFileId: this.friendsFileId
        })
      });

      this.successMessage = response.message || 'Friends import started successfully';
      this.friendsFileId = null;
      this.friendsFileName = null;
      await this.loadJobs();
      this.startPolling();
      this.isStartingImport = false;
      this.render();
    } catch (error) {
      logger.error('Failed to start friends import:', error);
      this.errorMessage = error.message || 'Failed to start friends import. Please try again.';
      this.isStartingImport = false;
      this.render();
    }
  }

  startPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }

    this.pollingInterval = setInterval(async () => {
      const hasActiveJobs = this.jobs.some(job =>
        job.status === 'pending' || job.status === 'processing'
      );

      if (hasActiveJobs) {
        await this.loadJobs();
        this.render();
      } else {
        if (this.pollingInterval) {
          clearInterval(this.pollingInterval);
          this.pollingInterval = null;
        }
        this.render();
      }
    }, 5000);
  }

  hasActiveJob() {
    return this.jobs.some(job => job.status === 'pending' || job.status === 'processing');
  }

  attachEventListeners() {
    // Use event delegation on the component itself
    // This works even after re-renders since the listener is on the component, not individual buttons
    if (!this._clickHandler) {
      this._clickHandler = (e) => {
        const target = e.target.closest('button');
        if (!target) return;

        // Handle pick friends file button
        if (target.id === 'pickFriendsFileBtn' && !target.disabled) {
          e.preventDefault();
          e.stopPropagation();
          this.openFilePicker();
          return;
        }

        // Handle start import button
        if (target.id === 'startFriendsImportBtn' && !target.disabled) {
          e.preventDefault();
          e.stopPropagation();
          this.startImport();
          return;
        }

        // Handle clear file button
        if (target.classList.contains('clear-file-btn')) {
          e.preventDefault();
          e.stopPropagation();
          this.friendsFileId = null;
          this.friendsFileName = null;
          this.render();
          return;
        }

        // Handle delete job button
        if (target.classList.contains('delete-job-btn')) {
          e.preventDefault();
          e.stopPropagation();
          const jobId = target.dataset.jobId;
          if (jobId && confirm('Are you sure you want to delete this import job?')) {
            this.deleteJob(jobId);
          }
          return;
        }

        // Handle return to settings button
        if (target.id === 'returnToSettingsBtn') {
          e.preventDefault();
          e.stopPropagation();
          window.router.navigate('/settings');
          return;
        }
      };
      this.addEventListener('click', this._clickHandler);
    }
  }

  async deleteJob(jobId) {
    try {
      await window.API.request(`/facebook-import/jobs/${jobId}`, {
        method: 'DELETE'
      });
      await this.loadJobs();
      this.render();
    } catch (error) {
      logger.error('Failed to delete job:', error);
      this.errorMessage = error.message || 'Failed to delete job';
      this.render();
    }
  }

  render() {
    const hasActiveJobs = this.hasActiveJob();
    const hasConnectedGoogleDrive = this.googleDriveAccounts.length > 0;

    this.innerHTML = `
      <div class="facebook-import">
        <div class="settings-header">
          ${renderPageTitle('👥', 'Import Facebook Friends')}
          <p>Import your Facebook friends from your Facebook export</p>
        </div>

        ${this.errorMessage ? `<div class="alert alert-error">${escapeHtml(this.errorMessage)}</div>` : ''}
        ${this.successMessage ? `<div class="alert alert-success">${escapeHtml(this.successMessage)}</div>` : ''}

        ${!hasConnectedGoogleDrive ? `
          <div class="help-warning">
            ⚠️ You need to connect a Google Drive account first (if you haven't already).
            <a href="/settings/sparkle-sources">Go to Sparkle Sources settings</a> to connect.
          </div>
        ` : ''}

        <div class="import-section">
          <h3>Select Friends File</h3>
          <p class="section-description">Select your <code>your_friends.json</code> file from your Facebook export.</p>
          
          <div class="form-group">
            <label>Friends JSON File</label>
            ${this.friendsFileId ? `
              <div class="selected-file">
                <span class="file-icon">📄</span>
                <span class="file-name">${escapeHtml(this.friendsFileName || 'Selected File')}</span>
                <button type="button" class="btn-clear-file clear-file-btn">Change</button>
              </div>
            ` : `
              <button
                type="button"
                id="pickFriendsFileBtn"
                class="btn btn-secondary"
                ${this.isStartingImport || this.isLoadingAccounts ? 'disabled' : ''}
              >
                ${this.isLoadingAccounts ? 'Loading...' : '📁 Pick Friends JSON File'}
              </button>
            `}
          </div>

          <button 
            type="button"
            id="startFriendsImportBtn"
            class="btn btn-primary"
            ${this.isStartingImport || hasActiveJobs || !this.friendsFileId ? 'disabled' : ''}
          >
            ${this.isStartingImport ? 'Starting Import...' : 'Start Friends Import'}
          </button>
        </div>

        ${hasActiveJobs ? '<p class="active-job-message">An import job is already in progress. Please wait or delete the active job.</p>' : ''}

        <div class="import-jobs-section">
          <h3>Your Import Jobs</h3>
          ${this.isLoading && !hasActiveJobs ? `
            <div class="loading-state">
              <div class="spinner"></div>
              <div>Loading import jobs...</div>
            </div>
          ` : this.jobs.length === 0 ? `
            <p class="empty-state">No import jobs found. Start one above!</p>
          ` : `
            <ul class="job-list">
              ${this.jobs.map(job => `
                <li class="job-item">
                  <div class="job-details">
                    <span class="job-id">#${job.id}</span>
                    <span class="job-status status-${job.status}">${escapeHtml(job.status)}</span>
                    <span class="job-created-at">${new Date(job.createdAt).toLocaleString()}</span>
                  </div>
                  <div class="job-progress">
                    <span class="progress-text">Friends: ${job.friendsFound || 0}</span>
                    ${job.status === 'processing' ? '<span class="spinner spinner-small"></span>' : ''}
                  </div>
                  ${job.errorMessage ? `<div class="job-error">Error: ${escapeHtml(job.errorMessage)}</div>` : ''}
                      <div class="job-actions">
                        <button class="btn btn-sm btn-danger delete-job-btn" data-job-id="${job.id}" title="Delete job">
                          ×
                        </button>
                      </div>
                </li>
              `).join('')}
            </ul>
          `}
        </div>

        <div class="settings-footer">
          <button class="btn btn-secondary" id="returnToSettingsBtn">
            ← Return to Settings
          </button>
        </div>
      </div>
    `;
  }
}

customElements.define('facebook-import-friends', FacebookImportFriends);

